import {
    CheckCircleIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XCircleIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../utils';
import { Children, ReactNode } from 'react';
import { ensureButtons } from '../Button/ensureButton.fn';

import Button from '../Button/Button';
import { Prettify } from '@types';

type AlertProps = {
    title: string;
    className?: string | object;
    children?: ReactNode;
    message?: string;
    dismissClick?: () => void;
};

export type Variant = {
    variant: 'success' | 'danger' | 'info' | 'warning';
};

export default function Alert({
    title,
    variant,
    className,
    message,
    dismissClick,
    children,
    ...rest
}: Prettify<AlertProps & Variant>) {
    const variantStyles = variants[variant];

    if (!ensureButtons(children)) {
        console.error('Children of <Alert> must be <Button>s');
        return null;
    }

    return (
        <div
            {...rest}
            className={cn(
                'alli-rounded-md alli-p-4',
                variantStyles.text,
                className,
            )}>
            <div className="alli-flex alli-justify-between">
                <div className="alli-flex">
                    <div className="alli-flex-shrink-0">
                        {variantStyles.icon}
                    </div>
                    <div className="alli-ml-3">
                        <h3 className="alli-text-sm alli-font-medium">
                            {title}
                        </h3>
                        {message && (
                            <div className="alli-mt-2 alli-text-sm">
                                <p>{message}</p>
                            </div>
                        )}

                        {Children.count(children) > 0 && (
                            <div className="alli-mt-4">
                                <div className="-alli-mx-2 -alli-my-1.5 alli-flex">
                                    {Children.toArray(children).map(
                                        (
                                            child: {
                                                props: {
                                                    onClick: () => void;
                                                    children: ReactNode;
                                                };
                                            },
                                            index: number,
                                        ) => {
                                            const { props } = child;
                                            return (
                                                <Button
                                                    key={`alert-button-${index}`}
                                                    onClick={() => {
                                                        props.onClick &&
                                                            props.onClick();
                                                    }}
                                                    variant="text"
                                                    className={cn(
                                                        'alli-rounded-md alli-px-2 alli-py-1.5 alli-text-sm alli-font-medium focus:alli-outline-none focus:alli-ring-2',
                                                        variantStyles.buttons,
                                                    )}>
                                                    {props.children}
                                                </Button>
                                            );
                                        },
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {dismissClick && (
                    <div className="alli-ml-auto alli-pl-3">
                        <div className="-alli-mx-1.5 -alli-my-1.5">
                            <button
                                data-testid="dismissButton"
                                type="button"
                                onClick={dismissClick}
                                className={variantStyles.dismissButton}>
                                <span className="alli-sr-only">Dismiss</span>
                                <XMarkIcon
                                    className="alli-h-5 alli-w-5"
                                    aria-hidden="true"
                                />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const variants = {
    success: {
        icon: (
            <CheckCircleIcon
                className="alli-h-5 alli-w-5 alli-stroke-2"
                aria-hidden="true"
            />
        ),
        text: 'alli-text-green-1000 alli-bg-green-200',
        buttons:
            'focus:alli-ring-green-1000 active:alli-text-green-1000 focus:alli-bg-green-1000/10 alli-text-green-1000 hover:alli-text-green-1000/75 focus-visible:alli-outline-green-1000',
        dismissButton:
            'focus:alli-ring-green-1000 focus:alli-ring-offset-green-200 alli-inline-flex alli-rounded-md alli-p-1.5 hover:alli-bg-green-200 focus:alli-outline-none focus:alli-ring-2 focus:alli-ring-offset-2',
    },
    danger: {
        icon: (
            <XCircleIcon
                className="alli-h-5 alli-w-5 alli-stroke-2"
                aria-hidden="true"
            />
        ),
        text: 'alli-text-red-600 alli-bg-red-100',
        buttons:
            'focus:alli-ring-red-600 active:alli-text-red-600 focus:alli-bg-red-600/10 alli-text-red-600 hover:alli-text-red-600 focus-visible:alli-outline-red-600',
        dismissButton:
            'focus:alli-ring-red-600 focus:alli-ring-offset-red-200 alli-inline-flex alli-rounded-md alli-p-1.5 focus:alli-outline-none focus:alli-ring-2 focus:alli-ring-offset-2',
    },
    info: {
        icon: (
            <InformationCircleIcon
                className="alli-h-5 alli-w-5 alli-stroke-2"
                aria-hidden="true"
            />
        ),
        text: 'alli-text-blue-800 alli-bg-blue-100',
        buttons:
            'focus:alli-ring-blue-600 active:alli-text-blue-800 focus:alli-bg-blue-600/10 alli-text-blue-800 hover:alli-text-blue-800 focus-visible:alli-outline-blue-600',
        dismissButton:
            'focus:alli-ring-blue-600 focus:alli-ring-offset-blue-200 alli-inline-flex alli-rounded-md alli-p-1.5 focus:alli-outline-none focus:alli-ring-2 focus:alli-ring-offset-2',
    },
    warning: {
        icon: (
            <ExclamationTriangleIcon
                className="alli-h-5 alli-w-5 alli-stroke-2"
                aria-hidden="true"
            />
        ),
        text: 'alli-text-orange-700 alli-bg-orange-200',
        buttons:
            'focus:alli-ring-orange-600 active:alli-text-orange-700 focus:alli-bg-orange-600/10 alli-text-orange-700 hover:alli-text-orange-700 focus-visible:alli-outline-orange-600',
        dismissButton:
            'focus:alli-ring-orange-600 focus:alli-ring-offset-orange-200 alli-inline-flex alli-rounded-md alli-p-1.5 hover:alli-bg-orange-200 focus:alli-outline-none focus:alli-ring-2 focus:alli-ring-offset-2',
    },
};
